import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { Token } from '@/types/token.type'
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { PaginateResponse } from '@/utils/paginate'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import { RedeemCondition, RedeemConditionType } from './privilege.types'

// Redeem type options
const redeemTypeOptions = [
  {
    value: RedeemConditionType.TOKEN,
    label: 'จ่ายด้วย Token',
  },
]

// Redeem condition schema (single)
const redeemConditionSchema = z
  .object({
    type: z.nativeEnum(RedeemConditionType, {
      errorMap: () => ({ message: 'กรุณาเลือกประเภทการแลก' }),
    }),
    amount: z
      .number({
        required_error: 'กรุณากรอกจำนวน',
        invalid_type_error: 'กรุณากรอกตัวเลขที่ถูกต้อง',
      })
      .min(0, 'จำนวนต้องไม่น้อยกว่า 0')
      .max(999999999, 'จำนวนไม่สามารถเกิน 999,999,999 ได้'),
    currency: z
      .string({
        required_error: 'กรุณากรอกสกุลเงิน',
      })
      .min(1, 'กรุณากรอกสกุลเงิน')
      .max(10, 'สกุลเงินไม่สามารถเกิน 10 ตัวอักษรได้')
      .regex(
        /^[A-Z]{3}$/,
        'สกุลเงินต้องเป็นตัวอักษรภาษาอังกฤษ 3 ตัว (เช่น THB, USD)',
      ),
    tokenId: z.string().optional(),
  })
  .refine(
    (data) => {
      // When type is TOKEN, tokenId is required
      if (data.type === RedeemConditionType.TOKEN) {
        return data.tokenId && data.tokenId.trim() !== ''
      }
      return true
    },
    {
      message: 'กรุณาเลือก Token',
      path: ['tokenId'],
    },
  )

export type RedeemConditionFormData = z.infer<typeof redeemConditionSchema>

// ----------------------------------------------------------------------

export interface RedeemConditionPayload {
  type: RedeemConditionType
  amount: number
  currency: string
  tokenId?: string
}

interface RedeemConditionFormProps {
  initialCondition?: RedeemCondition
  onSubmit: (condition: RedeemConditionPayload) => Promise<void>
  onCancel?: () => void
}

export default function RedeemConditionForm({
  initialCondition,
  onSubmit,
  onCancel,
}: RedeemConditionFormProps) {
  const [tokens, setTokens] = useState<Token[]>([])
  const [loadingTokens, setLoadingTokens] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = Boolean(initialCondition?.id)

  const form = useForm<RedeemConditionFormData>({
    resolver: zodResolver(redeemConditionSchema),
    defaultValues: {
      type: initialCondition?.type ?? redeemTypeOptions[0].value,
      amount: initialCondition?.amount ?? 0,
      currency: initialCondition?.currency ?? 'THB',
      tokenId: initialCondition?.tokenId ?? '',
    },
    mode: 'onChange', // Validate on change for better UX
  })

  // Check if form is dirty (has changes) in edit mode
  const isDirty = form.formState.isDirty
  const hasChanges = isEditMode ? isDirty : true // Always allow submit in create mode

  // Reset form to original values (useful for cancel or reset)
  const resetToOriginal = () => {
    if (initialCondition) {
      form.reset({
        type: initialCondition.type,
        amount: initialCondition.amount,
        currency: initialCondition.currency,
        tokenId: initialCondition.tokenId ?? '',
      })
    }
  }

  // Watch type to conditionally show fields
  const type = form.watch('type')
  const tokenId = form.watch('tokenId')

  // Fetch tokens on component mount
  useEffect(() => {
    fetchTokens()
  }, [])

  // Sync currency with selected token when type is TOKEN
  useEffect(() => {
    if (type === RedeemConditionType.TOKEN) {
      const tokenName = tokens.find((t) => t.id === tokenId)?.name
      if (tokenName) {
        form.setValue('currency', tokenName, { shouldValidate: true })
      }
    }
  }, [type, tokenId, tokens, form])

  const fetchTokens = async () => {
    try {
      setLoadingTokens(true)
      const { data } = await axios.get<PaginateResponse<Token>>(
        endpoints.tokens.list,
        {
          params: {
            page: 1,
            limit: 100,
          },
        },
      )
      setTokens(data.items)
    } catch {
      // no-op
    } finally {
      setLoadingTokens(false)
    }
  }

  const handleSubmit = async (data: RedeemConditionFormData) => {
    try {
      setIsSubmitting(true)

      // Validate the data with Zod before submission
      const validatedData = redeemConditionSchema.parse(data)

      // Transform currency for TOKEN type
      if (validatedData.type === RedeemConditionType.TOKEN) {
        validatedData.currency =
          tokens.find((token) => token.id === validatedData.tokenId)?.name ||
          '-'
      }

      await onSubmit(validatedData)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Check if form is valid for submission using Zod and has changes
  const isFormValid = () => {
    try {
      redeemConditionSchema.parse(form.getValues())
      return hasChanges // Must have changes in edit mode
    } catch {
      return false
    }
  }

  return (
    <Card className="border rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold text-black">
          {isEditMode ? 'แก้ไขเงื่อนไขการแลก' : 'เพิ่มเงื่อนไขการแลก'}
          {isEditMode && isDirty && (
            <span className="ml-2 text-xs text-orange-600 font-normal">
              (มีการเปลี่ยนแปลง)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Redeem Type */}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-black">
                      ประเภทการแลก
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-lg border">
                          <SelectValue placeholder="เลือกประเภทการแลก" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {redeemTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Redeem Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-black">
                      จำนวน Token
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="กรอกจำนวนเงินหรือ Token"
                        className="rounded-lg border"
                        min="0"
                        step="1"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === '') {
                            field.onChange(0)
                          } else {
                            const numValue = Number(value)
                            field.onChange(isNaN(numValue) ? 0 : numValue)
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Token Selection - Only show when TOKEN type */}
              {type === RedeemConditionType.TOKEN && (
                <FormField
                  control={form.control}
                  name="tokenId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-black">
                        เลือก Token
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={loadingTokens}
                      >
                        <FormControl>
                          <SelectTrigger className="rounded-lg border">
                            <SelectValue
                              placeholder={
                                loadingTokens ? 'กำลังโหลด...' : 'เลือก Token'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingTokens ? (
                            <SelectItem value="loading">
                              กำลังโหลด...
                            </SelectItem>
                          ) : (
                            tokens.map((token) => (
                              <SelectItem key={token.id} value={token.id}>
                                {token.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Redeem Currency - Always show; disable when TOKEN */}
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-black">
                      สกุลเงิน
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="THB, USD, etc."
                        className="rounded-lg border"
                        disabled={type === RedeemConditionType.TOKEN}
                        maxLength={3}
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase()
                          field.onChange(value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="submit"
                disabled={!isFormValid() || isSubmitting}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {isEditMode ? 'กำลังอัปเดต...' : 'กำลังเพิ่ม...'}
                  </>
                ) : isEditMode ? (
                  'อัปเดตเงื่อนไข'
                ) : (
                  'เพิ่มเงื่อนไข'
                )}
              </Button>

              {/* Reset button - only show in edit mode when there are changes */}
              {isEditMode && isDirty && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetToOriginal}
                  disabled={isSubmitting}
                >
                  รีเซ็ต
                </Button>
              )}

              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  ยกเลิก
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
